import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Skeleton } from 'v5/design-system/Skeleton';
import { cn } from 'v5/platform/dom/cn';
import { DataCard, DataCardHeader, DataCardHeaderContent } from './DataCard';
import { RowShell } from './RowShell';
import { TileBox } from './TileBox';
import { TileCollection, type TileState } from './TileCollection';
import { TileShell } from './TileShell';
import type { TileLayout } from './tileLayouts';

/** Tiles drawn before the area is measured, and the most it ever draws. */
const FIRST_COUNT = 12;
const MOST = 200;

/**
 * The collection while its items load: placeholder tiles drawn by `TileCollection` itself, in the
 * `layout` the loaded one will take (the same object, or one built from the same parameters), so
 * every placeholder stands where its tile will. As many as fill what is visible of it, never more
 * than `count`. Nothing in it takes a pointer or the keyboard's focus.
 */
export function TileCollectionSkeleton({
  label,
  layout,
  count,
  renderTile,
  className,
  'data-testid': testId
}: {
  /** What loads, for a screen reader ("Loading pins"). */
  label: string;
  layout: TileLayout;
  /** How many will come, when the host knows (a pin count). */
  count?: number;
  /** One placeholder: `DataCardSkeleton`, `ImmersiveCardSkeleton`, `RowSkeleton`, or the host's own on `SkeletonTile`. */
  renderTile(tile: TileState): ReactElement;
  /** The loaded collection's own. */
  className?: string;
  'data-testid'?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const shown = useFillCount(box, count ?? MOST);
  // React 18 drops a boolean `inert`
  useLayoutEffect(() => box.current?.setAttribute('inert', ''), []);
  const items = Array.from({ length: shown }, (_, index) => String(index));
  return (
    // `contents`: the layout's boxes stand in the host as the loaded collection's do
    <div role="status" aria-label={label} className="contents">
      <div ref={box} className="contents">
        <TileCollection
          label={label}
          items={items}
          keyOf={key => key}
          textOf={() => label}
          layout={layout}
          readOnly
          renderTile={(_, tile) => renderTile(tile)}
          className={className}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

/**
 * How many tiles fill what is visible of the list (inside the viewport and every box that clips
 * it, the list's own scroller too), at most `most`: drawn doubling until the last one falls out of
 * view, then cut to the ones in view; again when anything around it changes size.
 */
function useFillCount(
  box: React.RefObject<HTMLDivElement | null>,
  most: number
) {
  const [count, setCount] = useState(() => Math.min(FIRST_COUNT, most));
  // cut to what is in view: grow again only once the space changes
  const settled = useRef(false);
  // the space it settled in: a box that resizes only because of the cut doesn't ask to grow again
  const settledSpace = useRef('');
  const [space, setSpace] = useState(0);

  useLayoutEffect(() => {
    const root = box.current;
    if (!root) return;
    const observer = new ResizeObserver(() => {
      const first = root.querySelector('[data-key]');
      if (first && spaceOf(first) === settledSpace.current) return;
      settled.current = false;
      setSpace(value => value + 1);
    });
    const watched = clippingBoxes(root.querySelector('[data-key]') ?? root);
    watched.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [box]);

  // React Aria draws a changed count a render later, and a virtualizer once it has measured
  // itself: measured in the next frame
  const waited = useRef(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => measure());
    return () => cancelAnimationFrame(frame);
  });

  function measure() {
    const root = box.current;
    if (!root || count === 0) return;
    const first = root.querySelector('[data-key]');
    if (!first) {
      // a box with no size draws none: given up after a few frames
      if (waited.current++ < 10) setSpace(value => value + 1);
      return;
    }
    const view = visibleRect(first);
    const inView = (element: Element) => {
      const rect = element.getBoundingClientRect();
      return (
        rect.top < view.bottom &&
        rect.left < view.right &&
        rect.bottom > view.top &&
        rect.right > view.left
      );
    };
    const tiles = [...root.querySelectorAll('[data-key]')];
    const last = tiles.find(
      tile => tile.getAttribute('data-key') === String(count - 1)
    );
    if (last && inView(last) && !settled.current) {
      if (count < most) setCount(Math.min(most, count * 2));
      return;
    }
    const needed =
      Math.max(
        -1,
        ...tiles
          .filter(inView)
          .map(tile => Number(tile.getAttribute('data-key')))
      ) + 1;
    settled.current = true;
    settledSpace.current = spaceOf(first);
    if (needed > 0 && needed < count) setCount(needed);
  }

  // a host that learns its count later draws no more than it
  return Math.min(count, most);
}

/** The element and every ancestor that clips what overflows it. */
function clippingBoxes(element: Element): Element[] {
  const boxes: Element[] = [];
  for (
    let at: Element | null = element.parentElement;
    at;
    at = at.parentElement
  ) {
    const style = getComputedStyle(at);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible')
      boxes.push(at);
  }
  return boxes.length > 0 ? boxes : [document.documentElement];
}

function spaceOf(element: Element) {
  const view = visibleRect(element);
  return `${view.right - view.left}x${view.bottom - view.top}`;
}

function visibleRect(element: Element) {
  return clippingBoxes(element).reduce(
    (view, clip) => {
      const rect = clip.getBoundingClientRect();
      return {
        top: Math.max(view.top, rect.top),
        left: Math.max(view.left, rect.left),
        bottom: Math.min(view.bottom, rect.bottom),
        right: Math.min(view.right, rect.right)
      };
    },
    { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth }
  );
}

/**
 * One placeholder on the shared tile (`TileShell`'s box and corners, none of its interaction),
 * around a placeholder body; `className` is the tile's own box as the loaded tile's (its proportion).
 */
export function SkeletonTile({
  tile,
  className,
  children
}: {
  tile: TileState;
  className?: string;
  children: ReactNode;
}) {
  return (
    <TileShell tile={tile} selectLabel="" className={className}>
      {children}
    </TileShell>
  );
}

/** A line of text while it loads: as tall as the line of `className`'s text, the bar in its middle. */
export function SkeletonLine({
  className,
  width = 'w-3/4'
}: {
  className?: string;
  width?: string;
}) {
  return (
    <div className={cn('flex h-lh items-center', className)}>
      <Skeleton className={cn('h-[0.7em]', width)} />
    </div>
  );
}

/**
 * `DataCard` with the default grid body while it loads: the picture's place in its proportion,
 * then the name and a line under it, in the header's paddings and line heights.
 */
export function DataCardSkeleton({
  tile,
  aspectRatio = '4/3'
}: {
  tile: TileState;
  /** The loaded picture's, as `DataCardImage` takes it. */
  aspectRatio?: string;
}) {
  return (
    <SkeletonTile tile={tile}>
      <DataCard>
        <Skeleton
          className="w-full shrink-0 rounded-none"
          style={{ aspectRatio }}
        />
        <DataCardHeader>
          <DataCardHeaderContent>
            <SkeletonLine className="font-semibold" />
            <SkeletonLine className="text-sm" width="w-1/2" />
          </DataCardHeaderContent>
        </DataCardHeader>
      </DataCard>
    </SkeletonTile>
  );
}

/** `ImmersiveCard` while it loads: the tile in its proportion, the picture's place filling it. */
export function ImmersiveCardSkeleton({
  tile,
  ratio = 1
}: {
  tile: TileState;
  /** The loaded card's, width over height; square without it. */
  ratio?: number;
}) {
  return (
    <SkeletonTile
      tile={{ ...tile, style: { ...tile.style, aspectRatio: ratio } }}
      className="min-h-0 min-w-0"
    >
      <TileBox className="relative min-h-0 flex-1 bg-bg-subtle">
        <Skeleton className="size-full rounded-none" />
      </TileBox>
    </SkeletonTile>
  );
}

/**
 * `RowShell` while it loads: the circle that picks where the loaded rows draw one, the picture,
 * the name and a line under it. `className` is the row's own box, as the loaded row's (its height
 * in a plain list).
 */
export function RowSkeleton({
  tile,
  picks = false,
  className
}: {
  tile: TileState;
  /** The loaded rows pick: their circle's place. */
  picks?: boolean;
  className?: string;
}) {
  return (
    <RowShell tile={tile} selectLabel="" dragLabel="" className={className}>
      {picks && <Skeleton className="size-6 shrink-0 rounded-full" />}
      <Skeleton className="size-8 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col">
        <SkeletonLine className="text-sm" width="w-40 max-w-full" />
        <SkeletonLine className="text-sm" width="w-56 max-w-full" />
      </div>
    </RowShell>
  );
}
